/**
 * sourcePassage — liaison note → passage du document (chantier C16).
 *
 * Une note `source` ou `commentary` créée depuis le DocReader porte un
 * `sourceRef` : nom du fichier, page ou CFI, périmètre… et une `fileUrl` qui
 * est une **SAS de 15 minutes** capturée à la création, donc morte à la
 * relecture. Rouvrir le document sur le passage cité demande :
 *
 * 1. de retrouver le fichier dans la liste courante des fichiers de périmètre
 *    (`resolveSourceFile` : par `fileId` stable, sinon par nom et périmètre
 *    pour les notes antérieures à C16) ;
 * 2. d'en dériver la cible de navigation (`passageTargetFromSourceRef` :
 *    page + hauteur estimée pour un PDF, CFI pour un EPUB) ;
 * 3. d'afficher un repère humain qui tient sur une ligne (`passageLabel`).
 *
 * Fonctions pures, sans store : le hook `useOpenSourcePassage` les orchestre.
 * Pendant de `webSource.ts` (C9) pour les documents du lecteur.
 */

import type { NoteSourceRef } from '../types/api';
import type { BlobFile } from '../services/perimetreFilesAPI';
import type { DocReaderFile, PassageTarget, SupportedFileType } from '../components/DocReader/types';
import { formatLocatorLabel } from '../components/DocReader/utils/noteTitle';
import { documentTitleFromFileName } from './fileNameLabel';

/** Formats que le lecteur sait afficher (les mêmes que `FilePanel`). */
const READER_FILE_TYPES: ReadonlySet<string> = new Set<SupportedFileType>(['pdf', 'epub', 'html', 'docx']);

type SourceRefLike = Pick<NoteSourceRef, 'fileType' | 'fileName'> & Partial<NoteSourceRef>;

/** La provenance désigne-t-elle un document que le lecteur peut rouvrir ? */
export function canOpenSourcePassage(sourceRef: SourceRefLike | null | undefined): sourceRef is SourceRefLike & { fileType: SupportedFileType } {
    return Boolean(sourceRef && sourceRef.fileName && READER_FILE_TYPES.has(sourceRef.fileType));
}

/**
 * Cible de navigation : le CFI (EPUB) prime, précis au passage ; sinon la page
 * (PDF) avec la hauteur estimée du passage ; sinon aucune — le document
 * s'ouvre au début.
 */
export function passageTargetFromSourceRef(sourceRef: Partial<NoteSourceRef>, text?: string): PassageTarget | null {
    const texte = text?.trim() ? { text: text.trim() } : {};
    if (sourceRef.cfi) return { kind: 'cfi', cfi: sourceRef.cfi, ...texte };
    if (sourceRef.page && sourceRef.page > 0) {
        return sourceRef.paragraphEstimate
            ? { kind: 'page', page: sourceRef.page, yPercent: sourceRef.paragraphEstimate, ...texte }
            : { kind: 'page', page: sourceRef.page, ...texte };
    }
    // Sans repère stable (C19 : extrait d'un EPUB, d'un HTML, d'un DOCX), le texte est l'ancre.
    if (texte.text) return { kind: 'text', text: texte.text, ...(sourceRef.chapter ? { chapter: sourceRef.chapter } : {}) };
    return null;
}

/**
 * Repère du badge : `p.12`, `p.12-13` (passage chevauchant deux pages),
 * `chap. 3`, sinon le titre du document (« Carnet de lecture », jamais
 * « Carnet_de_lecture.html »). Même écriture que le titre de la note
 * (`formatLocatorLabel`), sans le libellé de chapitre ni le CFI brut : un
 * badge tient sur une ligne.
 */
export function passageLabel(sourceRef: SourceRefLike): string {
    const label = formatLocatorLabel({ page: sourceRef.page, pageEnd: sourceRef.pageEnd, chapter: sourceRef.chapter });
    return label || documentTitleFromFileName(sourceRef.fileName);
}

/**
 * Retrouve le fichier de périmètre désigné par la provenance dans la liste
 * courante (URLs SAS fraîches) : par identifiant stable, sinon par nom affiché
 * en préférant le périmètre du `sourceRef`, sinon par nom seul.
 */
export function resolveSourceFile(files: readonly BlobFile[], sourceRef: SourceRefLike): BlobFile | null {
    if (sourceRef.fileId) {
        const byId = files.find(f => f.id === sourceRef.fileId);
        if (byId) return byId;
    }
    const byName = files.filter(f => f.metadata?.name === sourceRef.fileName);
    if (byName.length === 0) return null;
    if (sourceRef.perimetreId) {
        const inPerimetre = byName.find(f => (f.metadata?.perimetreId ?? f.perimetreId) === sourceRef.perimetreId);
        if (inPerimetre) return inPerimetre;
    }
    return byName[0];
}

/**
 * Provenance d'une citation `document` de l'assistant (C19), dans la forme
 * d'une `NoteSourceRef` : le même chemin que la note source rouvre le document
 * sur le passage. `fileUrl` est vide (la SAS se résout par `fileId`). `null`
 * sans fichier ni nom.
 */
export function sourceRefDeCitation(citation: {
    sourceType?: string; fileId?: string; sourceFileName?: string; fileType?: string;
    sourcePage?: number; sourceChapter?: number; sourceChapterLabel?: string; sourceParagraphEstimate?: number;
}): NoteSourceRef | null {
    if (citation.sourceType !== 'document' || !citation.fileId || !citation.sourceFileName) return null;
    const fileType = citation.fileType && READER_FILE_TYPES.has(citation.fileType)
        ? (citation.fileType as SupportedFileType)
        : fileTypeDuNom(citation.sourceFileName);
    if (!fileType) return null;
    return {
        fileId: citation.fileId,
        fileName: citation.sourceFileName,
        fileUrl: '',
        fileType,
        ...(citation.sourcePage ? { page: citation.sourcePage } : {}),
        ...(citation.sourceChapter ? { chapter: citation.sourceChapter } : {}),
        ...(citation.sourceChapterLabel ? { chapterLabel: citation.sourceChapterLabel } : {}),
        ...(citation.sourceParagraphEstimate ? { paragraphEstimate: citation.sourceParagraphEstimate } : {}),
    };
}

function fileTypeDuNom(fileName: string): SupportedFileType | null {
    const ext = fileName.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? '';
    if (ext === 'htm' || ext === 'html' || ext === 'xhtml') return 'html';
    return READER_FILE_TYPES.has(ext) ? (ext as SupportedFileType) : null;
}

type NoeudLexical = { type?: unknown; text?: unknown; children?: unknown };

/** Le texte d'un nœud et de ses descendants (nœuds texte et marques, qui portent `text`). */
function texteDuNoeud(noeud: NoeudLexical): string {
    const propre = typeof noeud.text === 'string' ? noeud.text : '';
    const enfants = Array.isArray(noeud.children) ? (noeud.children as NoeudLexical[]).map(texteDuNoeud).join('') : '';
    return propre + enfants;
}

/** Les nœuds `source-line` et `quote` du JSON, dans l'ordre (sans descendre dans un nœud retenu). */
function noeudsCites(json: unknown): NoeudLexical[] {
    const retenus: NoeudLexical[] = [];
    const visiter = (n: unknown): void => {
        if (!n || typeof n !== 'object') return;
        const noeud = n as NoeudLexical;
        if (noeud.type === 'source-line' || noeud.type === 'quote') { retenus.push(noeud); return; }
        if (Array.isArray(noeud.children)) noeud.children.forEach(visiter);
    };
    visiter((json as { root?: unknown } | null | undefined)?.root);
    return retenus;
}

const LIGNE_CONCEPTS_DE = /^Concepts de /;

/**
 * Le texte cité d'une note, à retrouver dans le document (C26 SP4, revue finale I4) : celui de ses lignes
 * source (`source-line`) ou de ses citations (`quote`) s'il y en a — une note `commentary` porte la citation
 * à côté du commentaire ; sinon `content.text` **sans** sa dernière ligne quand elle commence par
 * « Concepts de » (la ligne que pose « Garder ce passage » d'une organisation n'est pas dans le document).
 */
export function texteDuPassage(note: { content?: { JSON?: unknown; text?: string } } | null | undefined): string {
    const cites = noeudsCites(note?.content?.JSON).map(n => texteDuNoeud(n).trim()).filter(Boolean);
    if (cites.length > 0) return cites.join(' ');
    const lignes = (note?.content?.text ?? '').split('\n');
    while (lignes.length > 0 && !lignes[lignes.length - 1].trim()) lignes.pop();
    if (lignes.length > 1 && LIGNE_CONCEPTS_DE.test(lignes[lignes.length - 1].trim())) lignes.pop();
    return lignes.join('\n').trim();
}

/** Le fichier ouvert dans le lecteur est-il celui de la provenance ? */
export function isSameSourceFile(current: DocReaderFile | null | undefined, sourceRef: SourceRefLike): boolean {
    if (!current) return false;
    if (current.id && sourceRef.fileId) return current.id === sourceRef.fileId;
    return Boolean(current.metadata?.name) && current.metadata?.name === sourceRef.fileName;
}
